import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import {
  uploadPartnerDocument,
  getKategorijeDokumentaPartnera,
  kreirajKategorijuDokumentaPartnera,
  obrisiKategorijuDokumentaPartnera
} from '../../lib/services/partnerDocumentService';
import Icon from '../AppIcon';

import DashboardSidebar from './DashboardSidebarAdmin';
import DashboardHeader from './DashboardHeader';
import DashboardFooter from './DashboardFooter';
import MobileMenuButton from './MobileMenuButton';

// ── Opcije za mjesece: vrijednosti (baza) su fiksne '01'..'12', labela dolazi iz t('months.<ItalianKey>') ──
const MONTH_KEYS = [
  { value: '', i18nKey: null },
  { value: '01', i18nKey: 'Gennaio' },
  { value: '02', i18nKey: 'Febbraio' },
  { value: '03', i18nKey: 'Marzo' },
  { value: '04', i18nKey: 'Aprile' },
  { value: '05', i18nKey: 'Maggio' },
  { value: '06', i18nKey: 'Giugno' },
  { value: '07', i18nKey: 'Luglio' },
  { value: '08', i18nKey: 'Agosto' },
  { value: '09', i18nKey: 'Settembre' },
  { value: '10', i18nKey: 'Ottobre' },
  { value: '11', i18nKey: 'Novembre' },
  { value: '12', i18nKey: 'Dicembre' },
];

const ITEMS_PER_PAGE = 15;

// ── Standardne tipologije: vrijednost (baza) je fiksni kod, labela dolazi iz t('dettagli_partner.types.<code>') ──
const STANDARD_CATEGORY_CODES = ['contract', 'invoice', 'other'];

const STORAGE_BUCKET = 'aziende';

// ── CUSTOM CONFIRM MODAL (hard delete) ──────────────────────────────────────
const ConfirmDeleteModal = ({ doc, onConfirm, onCancel, deleting, t }) => (
  <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-card border border-border p-6 rounded-2xl max-w-sm w-full shadow-2xl space-y-5 animate-in fade-in zoom-in duration-200">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-destructive/10 text-destructive rounded-xl">
          <Icon name="Trash2" size={22} />
        </div>
        <h3 className="font-black uppercase text-sm tracking-wider">
          {t('dettagli_partner.confirm_delete_title', { defaultValue: 'Conferma Eliminazione' })}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {t('dettagli_partner.confirm_delete', { defaultValue: "Sei sicuro di voler eliminare permanentemente questo documento? L'azione rimuoverà il file anche dallo storage." })}
        {doc?.nome_file && (
          <>
            <br />
            <span className="font-bold text-foreground break-all">"{doc.nome_file}"</span>
          </>
        )}
      </p>
      <div className="flex gap-3 pt-1">
        <button
          onClick={onCancel}
          disabled={deleting}
          className="flex-1 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-all disabled:opacity-50"
        >
          {t('common.no', { defaultValue: 'No' })}
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          className="flex-1 py-2.5 bg-destructive text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {deleting
            ? <><Icon name="Loader2" size={14} className="animate-spin" /> {t('common.delete', { defaultValue: 'Elimina' })}...</>
            : <>{t('common.yes', { defaultValue: 'Sì' })}, {t('common.delete', { defaultValue: 'Elimina' })}</>
          }
        </button>
      </div>
    </div>
  </div>
);

// NAPOMENA: Interni "Anteprima" modal je potpuno uklonjen — pregled se otvara
// direktno u novom tabu preko Supabase Signed URL-a. Nema Bloba, nema
// URL.createObjectURL/revokeObjectURL za ovu funkcionalnost.

// ── GLAVNA KOMPONENTA ────────────────────────────────────────────────────────
const DettagliPartner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams(); // id_partnera

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [partnerData, setPartnerData] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [customKategorije, setCustomKategorije] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Kategorije modal
  const [isNovaKatModalOpen, setIsNovaKatModalOpen] = useState(false);
  const [novaKategorijaNaziv, setNovaKategorijaNaziv] = useState('');

  // Upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Info modal
  const [selectedDocForInfo, setSelectedDocForInfo] = useState(null);
  const [isEditingDoc, setIsEditingDoc] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [editForm, setEditForm] = useState({
    tipologia: '', anno: '', mese: '', valido_dal: '', valido_al: '', refid: '', note: ''
  });

  // Confirm delete modal (hard delete)
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Anteprima
  const [anteorimaLoading, setAnteorimaLoading] = useState(false);

  // Download
  const [downloadingId, setDownloadingId] = useState(null);

  // Upload form
  const [file, setFile] = useState(null);
  const [tipologia, setTipologia] = useState(STANDARD_CATEGORY_CODES[0]);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [mese, setMese] = useState('');
  const [refid, setRefid] = useState('');
  const [dataValidaDal, setDataValidaDal] = useState('');
  const [dataValidaAl, setDataValidaAl] = useState('');
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);

  // Filteri
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnno, setFilterAnno] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');

  // ── Helpers za prevod dinamičkih vrijednosti ────────────────────────────────
  const getMonthLabel = useCallback((value) => {
    const found = MONTH_KEYS.find(m => m.value === value);
    if (!found || !found.i18nKey) return '';
    return t(`months.${found.i18nKey}`, { defaultValue: found.i18nKey });
  }, [t]);

  const getTipologiaLabel = useCallback((code) => {
    if (!code) return '';
    if (STANDARD_CATEGORY_CODES.includes(code)) {
      return t(`dettagli_partner.types.${code}`, { defaultValue: code });
    }
    // Custom (korisnički definisana) kategorija — nema prevoda, prikazuje se izvorni naziv
    return code;
  }, [t]);

  // ── Fetch data ─────────────────────────────────────────────────────────────
  const fetchInitialData = useCallback(async (pageNumber = 0) => {
    if (pageNumber === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error: profileErr } = await supabase
        .from('profili').select('*').eq('id', user.id).single();
      if (profileErr) throw profileErr;
      setUserData(profile);
      if (!profile?.id_azienda) { setLoading(false); return; }

      // Osnovni podaci o partneru — filtrirano i po id_azienda kao sigurnosna mjera
      // (firma ne može pregledati partnera koji pripada drugoj firmi)
      const { data: partner, error: partnerErr } = await supabase
        .from('partneri')
        .select('*')
        .eq('id', id)
        .eq('id_aziende', profile.id_azienda)
        .single();
      if (partnerErr) throw partnerErr;
      setPartnerData(partner);

      const from = pageNumber * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data: docs, error: docsErr } = await supabase
        .from('documenti_partnera')
        .select('*')
        .eq('id_partnera', id)
        .eq('id_azienda', profile.id_azienda)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .range(from, to);
      if (docsErr) throw docsErr;

      if (pageNumber === 0) setDocumenti(docs || []);
      else setDocumenti(prev => [...prev, ...(docs || [])]);
      setHasMore((docs || []).length === ITEMS_PER_PAGE);

      // IZOLACIJA PO PARTNERU: kategorije se dohvataju SAMO za ovog partnera
      // (profile.id_azienda + id, ne samo profile.id_azienda kao ranije).
      const katResult = await getKategorijeDokumentaPartnera(profile.id_azienda, id);
      if (katResult.success) setCustomKategorije(katResult.data);
    } catch (err) {
      console.error('Errore fetch dettagli partner:', err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [id]);

  useEffect(() => { fetchInitialData(0); }, [fetchInitialData]);

  // ── Kategorije ─────────────────────────────────────────────────────────────
  // IZOLACIJA PO PARTNERU: nova kategorija se vezuje i za id_azienda i za
  // id_partnera (iz useParams). Provjera duplikata (standardni tipovi +
  // customKategorije) automatski je već skopirana na ovog partnera, jer je
  // customKategorije state popunjen isključivo kategorijama ovog partnera.
  const handleDodajKategoriju = async () => {
    const naziv = novaKategorijaNaziv.trim();
    if (!naziv) return;
    const standardLabels = STANDARD_CATEGORY_CODES.map(c => getTipologiaLabel(c).toLowerCase());
    const customLabels = customKategorije.map(k => k.naziv.toLowerCase());
    if ([...standardLabels, ...customLabels].includes(naziv.toLowerCase())) {
      return alert(t('dettagli_partner.category_exists', { defaultValue: 'Questa categoria esiste già!' }));
    }
    const result = await kreirajKategorijuDokumentaPartnera(naziv, userData.id_azienda, id);
    if (result.success) {
      setCustomKategorije(prev => [...prev, result.data]);
      setTipologia(result.data.naziv);
      setNovaKategorijaNaziv('');
      setIsNovaKatModalOpen(false);
    } else {
      alert(t('dettagli_partner.category_error', { defaultValue: 'Errore' }) + ': ' + result.error);
    }
  };

  const handleDeleteKategorija = async (katId, katNaziv) => {
    if (!window.confirm(t('dettagli_partner.confirm_delete_category', { defaultValue: 'Vuoi eliminare questa categoria?' }))) return;
    const result = await obrisiKategorijuDokumentaPartnera(katId, userData.id_azienda, id);
    if (result.success) {
      setCustomKategorije(prev => prev.filter(k => k.id !== katId));
      if (tipologia === katNaziv) setTipologia(STANDARD_CATEGORY_CODES[0]);
    } else {
      alert(result.error);
    }
  };

  // ── Upload ─────────────────────────────────────────────────────────────────
  // uploadPartnerDocument() upisuje SVA metapodatka polja u JEDNOM insert
  // pozivu (tipologia, anno, mese, refid, valido_dal, valido_al, note) —
  // nije potreban dodatni UPDATE poziv poslije uploada.
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return alert(t('dettagli_partner.select_file_alert', { defaultValue: 'Seleziona un file!' }));
    if (!userData?.id_azienda) return alert(t('dettagli_partner.missing_company', { defaultValue: 'Dato aziendale mancante.' }));
    if (!id) return alert(t('dettagli_partner.missing_partner', { defaultValue: 'Partner non trovato.' }));

    setUploading(true);
    try {
      const res = await uploadPartnerDocument({
        file,
        idAzienda: userData.id_azienda,
        idPartnera: id,
        uploadedBy: userData.id,
        tipologia,
        anno,
        mese: mese || null,
        refid: refid || null,
        validoDal: dataValidaDal || null,
        validoAl: dataValidaAl || null,
        note: note || null
      });

      if (!res?.success) {
        throw new Error(res?.error || t('dettagli_partner.upload_error', { defaultValue: 'Errore durante il caricamento' }));
      }

      // Reset forme
      setFile(null);
      setMese('');
      setRefid('');
      setDataValidaDal('');
      setDataValidaAl('');
      setNote('');
      setTipologia(STANDARD_CATEGORY_CODES[0]);
      setAnno(new Date().getFullYear());

      alert(t('dettagli_partner.upload_success', { defaultValue: 'Documento caricato con successo!' }));

      setIsUploadModalOpen(false);
      fetchInitialData(0);
      setPage(0);
    } catch (err) {
      alert(t('dettagli_partner.upload_error', { defaultValue: 'Errore durante il caricamento' }) + ': ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Download ───────────────────────────────────────────────────────────────
  const handleDownload = async (docId, path, nomeFile) => {
    let blobUrl = null;
    try {
      setDownloadingId(docId);
      const { data, error } = await supabase.storage.from(STORAGE_BUCKET).download(path);
      if (error) throw error;
      blobUrl = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', nomeFile);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(t('dettagli_partner.download_error', { defaultValue: 'Impossibile scaricare il file.' }) + ': ' + err.message);
    } finally {
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
      setDownloadingId(null);
    }
  };

  // ── Anteprima: direktno otvaranje u JEDNOM tabu, bez pop-up blokade ────────
  const handleAnteprima = async (doc) => {
    if (anteorimaLoading) return;
    setAnteorimaLoading(true);

    const newTab = window.open('', '_blank');

    try {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(doc.file_path, 60);
      if (error) throw error;

      if (newTab) {
        newTab.location.href = data.signedUrl;
      } else {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err) {
      if (newTab) newTab.close();
      alert(t('dettagli_partner.preview_error', { defaultValue: 'Errore durante il caricamento dell\'anteprima.' }) + ': ' + err.message);
    } finally {
      setAnteorimaLoading(false);
    }
  };

  // ── Info modal ─────────────────────────────────────────────────────────────
  const handleOpenInfoModal = (doc) => {
    setSelectedDocForInfo(doc);
    setIsEditingDoc(false);
    setEditForm({
      tipologia: doc.tipologia || STANDARD_CATEGORY_CODES[0],
      anno: doc.anno || new Date().getFullYear(),
      mese: doc.mese || '',
      valido_dal: doc.valido_dal || '',
      valido_al: doc.valido_al || '',
      refid: doc.refid || '',
      note: doc.note || ''
    });
  };

  const handleSaveDocEdits = async () => {
    if (!selectedDocForInfo) return;
    setSavingDoc(true);
    try {
      const updatedFields = {
        tipologia: editForm.tipologia,
        anno: parseInt(editForm.anno, 10) || new Date().getFullYear(),
        mese: editForm.mese || null,
        valido_dal: editForm.valido_dal || null,
        valido_al: editForm.valido_al || null,
        refid: editForm.refid || null,
        note: editForm.note || null
      };

      const { data, error } = await supabase
        .from('documenti_partnera')
        .update(updatedFields)
        .eq('id', selectedDocForInfo.id)
        .select().single();
      if (error) throw error;

      setDocumenti(prev => prev.map(d => d.id === data.id ? data : d));
      setSelectedDocForInfo(data);
      setIsEditingDoc(false);
      alert(t('dettagli_partner.update_success', { defaultValue: 'Documento aggiornato con successo!' }));
    } catch (err) {
      alert(t('dettagli_partner.update_error', { defaultValue: "Errore durante l'aggiornamento" }) + ': ' + err.message);
    } finally {
      setSavingDoc(false);
    }
  };

  // ── Delete: FIZIČKO (HARD) BRISANJE ─────────────────────────────────────────
  const confirmDelete = async () => {
    if (!docToDelete) return;
    setDeletingId(docToDelete.id);
    try {
      if (docToDelete.file_path) {
        const { error: storageErr } = await supabase.storage
          .from(STORAGE_BUCKET)
          .remove([docToDelete.file_path]);
        if (storageErr) {
          console.warn('Upozorenje: brisanje fajla iz storage-a nije uspjelo:', storageErr.message);
        }
      }

      const { error: dbErr } = await supabase
        .from('documenti_partnera')
        .delete()
        .eq('id', docToDelete.id);
      if (dbErr) throw dbErr;

      setDocumenti(prev => prev.filter(d => d.id !== docToDelete.id));
      if (selectedDocForInfo?.id === docToDelete.id) setSelectedDocForInfo(null);
      setDocToDelete(null);
    } catch (err) {
      alert(t('dettagli_partner.delete_error', { defaultValue: "Errore durante l'eliminazione" }) + ': ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Memoized values ────────────────────────────────────────────────────────
  const uniqueYears = useMemo(() => {
    const years = documenti.map(d => d.anno?.toString()).filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a);
  }, [documenti]);

  const realnoPostojeceKategorije = useMemo(() => {
    const kats = documenti.map(d => d.tipologia).filter(Boolean);
    return [...new Set(kats)].sort();
  }, [documenti]);

  const filteredDocumenti = useMemo(() => {
    return documenti.filter(doc => {
      const s = searchTerm.toLowerCase();
      const dateStr = new Date(doc.created_at).toLocaleDateString(
        i18n.language === 'bs' ? 'bs-BA' : i18n.language
      );
      const tipologiaLabel = getTipologiaLabel(doc.tipologia).toLowerCase();
      const matchesSearch =
        doc.nome_file?.toLowerCase().includes(s) ||
        tipologiaLabel.includes(s) ||
        doc.refid?.toLowerCase().includes(s) ||
        doc.note?.toLowerCase().includes(s) ||
        doc.anno?.toString().includes(s) ||
        dateStr.includes(s);
      const matchesAnno = filterAnno === 'all' || doc.anno?.toString() === filterAnno;
      const matchesTipo = filterTipo === 'all' || doc.tipologia === filterTipo;
      return matchesSearch && matchesAnno && matchesTipo;
    });
  }, [documenti, searchTerm, filterAnno, filterTipo, i18n.language, getTipologiaLabel]);

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center text-primary">
        <Icon name="Loader2" size={40} className="animate-spin mb-4" />
        <p className="text-muted-foreground text-xs font-black uppercase tracking-widest">
          {t('common.loading', { defaultValue: 'Caricamento...' })}
        </p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardHeader userName={userData?.nome ? `${userData.nome} ${userData.cognome}` : 'Admin'} />

      <main className="main-content with-sidebar py-10 px-4 md:px-8 mt-16">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* Zaglavlje */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-bold uppercase text-[10px] tracking-widest"
            >
              <div className="p-2 bg-muted rounded-xl group-hover:bg-primary/10 transition-colors">
                <Icon name="ArrowLeft" size={16} />
              </div>
              {t('dettagli_partner.back', { defaultValue: 'Indietro' })}
            </button>
          </div>

          {/* Naslov + dugme "Novi Upload" na desnoj strani */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Icon name="Handshake" className="text-primary" size={32} />
                {t('dettagli_partner.gestione_documenti', { defaultValue: 'Gestione Documenti per' })}: {partnerData?.naziv_partnera}
              </h1>
              <p className="text-xs text-muted-foreground mt-1 font-medium">
                {t('dettagli_partner.subtitle', { defaultValue: 'Gestione e archiviazione digitale dei documenti del partner' })}
              </p>
              <div className="h-1 w-20 bg-primary mt-3 rounded-full"></div>
            </div>

            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="shrink-0 flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-2xl font-bold uppercase text-xs tracking-widest hover:opacity-90 transition-all shadow-lg shadow-primary/20"
            >
              <Icon name="UploadCloud" size={18} />
              {t('dettagli_partner.new_upload', { defaultValue: 'Nuovo Upload' })}
            </button>
          </div>

          {/* Sadržaj: Tabela (full width) */}
          <div className="space-y-4">

            {/* Filteri */}
            <div className="bg-card p-4 rounded-2xl border border-border shadow-sm flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-[2] w-full">
                <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">
                  {t('dettagli_partner.filter_archive', { defaultValue: 'Filtra Archivio' })}
                </label>
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                  <input
                    type="text"
                    placeholder={t('dettagli_partner.search_placeholder', { defaultValue: 'Cerca per nome, tipo, N. riferimento...' })}
                    className="w-full pl-11 pr-4 py-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none focus:bg-background transition-all font-medium"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1 w-full grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">
                    {t('dettagli_partner.year', { defaultValue: 'Anno' })}
                  </label>
                  <select
                    className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none font-bold"
                    value={filterAnno}
                    onChange={e => setFilterAnno(e.target.value)}
                  >
                    <option value="all">{t('common.all', { defaultValue: 'Tutti' })}</option>
                    {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">
                    {t('dettagli_partner.type', { defaultValue: 'Tipologia' })}
                  </label>
                  <select
                    className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none font-bold truncate"
                    value={filterTipo}
                    onChange={e => setFilterTipo(e.target.value)}
                  >
                    <option value="all">{t('common.all', { defaultValue: 'Tutti' })}</option>
                    {realnoPostojeceKategorije.map(kat => (
                      <option key={kat} value={kat}>{getTipologiaLabel(kat)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
              <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2 uppercase text-xs tracking-widest text-muted-foreground">
                  <Icon name="FileText" size={16} className="text-primary" />
                  {t('dettagli_partner.list_title', { defaultValue: 'Elenco Documenti Caricati' })}
                </h3>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-1 rounded-lg">
                  Total: {filteredDocumenti.length}
                </span>
              </div>

              <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
                {filteredDocumenti.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">
                    <Icon name="FolderOpen" size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-xs font-bold uppercase tracking-widest">
                      {t('dettagli_partner.no_docs_found', { defaultValue: 'Nessun documento trovato per i criteri selezionati.' })}
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-muted/30 text-[10px] uppercase font-black tracking-widest text-muted-foreground">
                        <th className="p-3.5 pl-4">{t('dettagli_partner.col_name', { defaultValue: 'Nome Documento' })}</th>
                        <th className="p-3.5">{t('dettagli_partner.col_ref', { defaultValue: 'Riferimento ID' })}</th>
                        <th className="p-3.5">{t('dettagli_partner.col_type', { defaultValue: 'Tipologia' })}</th>
                        <th className="p-3.5">{t('dettagli_partner.col_period', { defaultValue: 'Periodo / Validità' })}</th>
                        <th className="p-3.5">{t('dettagli_partner.valid_from', { defaultValue: 'Valido Dal' })}</th>
                        <th className="p-3.5">{t('dettagli_partner.valid_to', { defaultValue: 'Valido Al' })}</th>
                        <th className="p-3.5 pr-4 text-right">{t('common.actions', { defaultValue: 'Azioni' })}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border text-sm">
                      {filteredDocumenti.map(doc => (
                        <tr key={doc.id} className="hover:bg-muted/20 transition-colors group">

                          <td className="p-3.5 pl-4 font-bold text-foreground">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0">
                                <Icon name="FileText" size={18} />
                              </div>
                              <div className="min-w-0">
                                <p className="truncate max-w-[160px] text-xs font-bold" title={doc.nome_file}>
                                  {doc.nome_file}
                                </p>
                                {doc.note && (
                                  <p className="text-[10px] text-muted-foreground font-normal truncate max-w-[150px]">
                                    {doc.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="p-3.5">
                            {doc.refid
                              ? <span className="text-[10px] font-bold font-mono bg-muted px-2 py-0.5 rounded-md border border-border">{doc.refid}</span>
                              : <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            }
                          </td>

                          <td className="p-3.5">
                            <span className="inline-block bg-muted text-foreground text-[10px] font-black uppercase px-2.5 py-1 rounded-md border border-border">
                              {getTipologiaLabel(doc.tipologia)}
                            </span>
                          </td>

                          <td className="p-3.5 font-bold text-xs">
                            <span>{doc.anno}</span>
                            {doc.mese && (
                              <span className="ml-1 text-[10px] text-muted-foreground font-bold">
                                / {getMonthLabel(doc.mese)}
                              </span>
                            )}
                          </td>

                          <td className="p-3.5 text-xs text-muted-foreground">
                            {doc.valido_dal
                              ? <span className="text-[10px] font-bold">{new Date(doc.valido_dal).toLocaleDateString()}</span>
                              : <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            }
                          </td>

                          <td className="p-3.5 text-xs text-muted-foreground">
                            {doc.valido_al ? (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                                new Date(doc.valido_al) < new Date()
                                  ? 'bg-destructive/10 text-destructive border border-destructive/20'
                                  : 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                              }`}>
                                {new Date(doc.valido_al).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            )}
                          </td>

                          <td className="p-3.5 pr-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleOpenInfoModal(doc)}
                                className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg text-muted-foreground transition-all"
                                title={t('common.edit', { defaultValue: 'Modifica' })}
                              >
                                <Icon name="Info" size={16} />
                              </button>
                              <button
                                onClick={() => handleDownload(doc.id, doc.file_path, doc.nome_file)}
                                disabled={downloadingId === doc.id}
                                className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg text-muted-foreground transition-all"
                                title={t('common.download', { defaultValue: 'Scarica' })}
                              >
                                {downloadingId === doc.id
                                  ? <Icon name="Loader2" size={16} className="animate-spin text-primary" />
                                  : <Icon name="Download" size={16} />
                                }
                              </button>
                              <button
                                onClick={() => setDocToDelete(doc)}
                                className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg text-muted-foreground transition-all"
                                title={t('common.delete', { defaultValue: 'Elimina' })}
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {hasMore && (
                <div className="p-3 border-t border-border text-center bg-muted/5">
                  <button
                    onClick={() => { const next = page + 1; setPage(next); fetchInitialData(next); }}
                    disabled={loadingMore}
                    className="text-xs font-black uppercase tracking-widest text-primary hover:underline flex items-center justify-center gap-2 mx-auto py-1"
                  >
                    {loadingMore && <Icon name="Loader2" size={14} className="animate-spin" />}
                    {t('common.load_more', { defaultValue: 'Carica altri' })}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter />

      {/* ── MODAL: Novi Upload (React Portal) — z-[9999] ────────────────────── */}
      {isUploadModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-background/60 backdrop-blur-md animate-in fade-in duration-300"
            onClick={() => setIsUploadModalOpen(false)}
          />

          <div className="relative bg-card border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-3 mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="PlusCircle" size={16} className="text-primary" />
                  <h3 className="text-xs font-black uppercase text-foreground tracking-widest">
                    {t('dettagli_partner.new_upload', { defaultValue: 'Nuovo Upload' })}
                  </h3>
                </div>
                <button
                  onClick={() => setIsUploadModalOpen(false)}
                  className="p-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>

              <form onSubmit={handleUpload} className="space-y-4">
                {/* Kategorija */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                      {t('dettagli_partner.type', { defaultValue: 'Tipologia' })}
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsNovaKatModalOpen(true)}
                      className="text-[10px] text-primary font-black uppercase tracking-wider hover:opacity-70 transition-all flex items-center gap-1"
                    >
                      <Icon name="Plus" size={10} />
                      {t('dettagli_partner.categ_title2', { defaultValue: 'Nuova Categoria' })}
                    </button>
                  </div>
                  <select
                    className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 font-bold"
                    value={tipologia}
                    onChange={e => setTipologia(e.target.value)}
                  >
                    {STANDARD_CATEGORY_CODES.map(code => (
                      <option key={code} value={code}>{getTipologiaLabel(code)}</option>
                    ))}
                    {customKategorije.map(kat => (
                      <option key={kat.id} value={kat.naziv}>{kat.naziv}</option>
                    ))}
                  </select>
                </div>

                {/* Godina + Mjesec */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                      {t('dettagli_partner.year', { defaultValue: 'Anno' })}
                    </label>
                    <input
                      type="number"
                      className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-bold"
                      value={anno}
                      onChange={e => setAnno(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                      {t('dettagli_partner.month', { defaultValue: 'Mese' })}
                    </label>
                    <select
                      className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 font-bold"
                      value={mese}
                      onChange={e => setMese(e.target.value)}
                    >
                      {MONTH_KEYS.map(m => (
                        <option key={m.value || 'none'} value={m.value}>
                          {m.i18nKey ? t(`months.${m.i18nKey}`, { defaultValue: m.i18nKey }) : '—'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Datum Važenja */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                      {t('dettagli_partner.valid_from', { defaultValue: 'Valido Dal' })}
                    </label>
                    <input
                      type="date"
                      className="w-full p-2.5 bg-background border border-border rounded-xl text-xs font-bold outline-none"
                      value={dataValidaDal}
                      onChange={e => setDataValidaDal(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                      {t('dettagli_partner.valid_to', { defaultValue: 'Valido Al' })}
                    </label>
                    <input
                      type="date"
                      className="w-full p-2.5 bg-background border border-border rounded-xl text-xs font-bold outline-none"
                      value={dataValidaAl}
                      onChange={e => setDataValidaAl(e.target.value)}
                    />
                  </div>
                </div>

                {/* Referentni ID */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                    {t('dettagli_partner.refid', { defaultValue: 'N. Riferimento / ID' })}
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-bold"
                    placeholder={t('dettagli_partner.refid_placeholder', { defaultValue: 'Es. FATT-2026-001' })}
                    value={refid}
                    onChange={e => setRefid(e.target.value)}
                  />
                </div>

                {/* Napomene */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-muted-foreground uppercase ml-1">
                    {t('dettagli_partner.notes', { defaultValue: 'Note / Descrizione' })}
                  </label>
                  <textarea
                    className="w-full p-2.5 bg-background border border-border rounded-xl text-xs outline-none min-h-[70px]"
                    placeholder={t('dettagli_partner.notes_placeholder', { defaultValue: 'Specifica di cosa si tratta...' })}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                  />
                </div>

                {/* File zone */}
                <div className="relative">
                  <input type="file" id="partner_file" className="hidden" onChange={e => setFile(e.target.files[0])} />
                  <label
                    htmlFor="partner_file"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-border p-6 rounded-2xl cursor-pointer hover:border-primary/40 hover:bg-primary/[0.02] transition-all"
                  >
                    <Icon name="UploadCloud" className="text-muted-foreground mb-2" size={32} />
                    <span className="text-xs font-bold text-muted-foreground text-center px-2 truncate max-w-xs">
                      {file ? file.name : t('dettagli_partner.select_pdf_img', { defaultValue: 'Seleziona PDF o Immagine' })}
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={uploading || !file}
                  className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-40 shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <><Icon name="Loader2" size={18} className="animate-spin" />{t('dettagli_partner.uploading', { defaultValue: 'Caricamento in corso...' })}</>
                  ) : t('dettagli_partner.upload_btn', { defaultValue: 'Carica Documento' })}
                </button>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── MODAL: Nova Kategorija (React Portal) — z-[10000] ─────────────── */}
      {isNovaKatModalOpen && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setIsNovaKatModalOpen(false)}
          />

          <div className="relative bg-card border border-border p-6 rounded-2xl max-w-md w-full space-y-4 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="font-black uppercase text-sm tracking-widest flex items-center gap-2">
                <Icon name="FolderPlus" className="text-primary" size={18} />
                {t('dettagli_partner.categ_title2', { defaultValue: 'Nuova Categoria' })}
              </h3>
              <button onClick={() => setIsNovaKatModalOpen(false)} className="text-muted-foreground hover:text-foreground p-1 rounded-lg">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                placeholder={t('dettagli_partner.categ_input_placeholder', { defaultValue: 'Nome categoria...' })}
                className="w-full p-3 bg-background border border-border rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-primary/20"
                value={novaKategorijaNaziv}
                onChange={e => setNovaKategorijaNaziv(e.target.value)}
              />
              {customKategorije.length > 0 && (
                <div className="pt-2">
                  <p className="text-[10px] font-black uppercase text-muted-foreground mb-2 tracking-widest">
                    {t('dettagli_partner.categ_custom_list', { defaultValue: 'Categorie personalizzate:' })}
                  </p>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {customKategorije.map(k => (
                      <span key={k.id} className="inline-flex items-center gap-1.5 bg-muted text-foreground text-[11px] font-bold px-2.5 py-1 rounded-lg border border-border">
                        {k.naziv}
                        <button onClick={() => handleDeleteKategorija(k.id, k.naziv)} className="hover:text-destructive transition-colors ml-1">
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setIsNovaKatModalOpen(false)} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                {t('common.cancel', { defaultValue: 'Annulla' })}
              </button>
              <button
                onClick={handleDodajKategoriju}
                disabled={!novaKategorijaNaziv.trim()}
                className="bg-primary text-primary-foreground px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-all disabled:opacity-40"
              >
                {t('dettagli_partner.categ_add', { defaultValue: 'Aggiungi' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── MODAL: Confirm Delete (Hard Delete) ─────────────────────────────── */}
      {docToDelete && (
        <ConfirmDeleteModal
          doc={docToDelete}
          onConfirm={confirmDelete}
          onCancel={() => setDocToDelete(null)}
          deleting={deletingId === docToDelete.id}
          t={t}
        />
      )}

      {/* ── MODAL: Info / Uređivanje ──────────────────────────────────────── */}
      {selectedDocForInfo && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-2xl max-w-lg w-full space-y-6 shadow-2xl animate-in fade-in zoom-in duration-200">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
                  <Icon name={isEditingDoc ? 'Edit3' : 'FileText'} size={22} />
                </div>
                <div>
                  <h3 className="font-black uppercase text-sm tracking-wider text-foreground">
                    {isEditingDoc ? t('dettagli_partner.edit_modal_title', { defaultValue: 'Modifica Documento' }) : t('dettagli_partner.info_modal_title', { defaultValue: 'Dettagli Documento' })}
                  </h3>
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest mt-0.5">
                    {isEditingDoc ? t('dettagli_partner.edit_subtitle', { defaultValue: 'Modifica parametri' }) : t('dettagli_partner.info_subtitle', { defaultValue: 'Panoramica dati' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!isEditingDoc && (
                  <button
                    onClick={() => setIsEditingDoc(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    <Icon name="Edit" size={14} /> {t('common.edit', { defaultValue: 'Modifica' })}
                  </button>
                )}
                <button
                  onClick={() => { setSelectedDocForInfo(null); setIsEditingDoc(false); }}
                  className="p-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            {/* Sadržaj */}
            <div className="space-y-4 text-xs max-h-[60vh] overflow-y-auto pr-1">
              <div className="bg-muted/30 p-3.5 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('dettagli_partner.col_name', { defaultValue: 'Nome Documento' })}
                </span>
                <p className="font-bold text-foreground break-all text-sm">{selectedDocForInfo.nome_file || t('common.nd', { defaultValue: 'N/D' })}</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('dettagli_partner.type', { defaultValue: 'Tipologia' })}
                  </span>
                  {isEditingDoc ? (
                    <select
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-primary/20"
                      value={editForm.tipologia}
                      onChange={e => setEditForm({ ...editForm, tipologia: e.target.value })}
                    >
                      {STANDARD_CATEGORY_CODES.map(code => (
                        <option key={code} value={code}>{getTipologiaLabel(code)}</option>
                      ))}
                      {customKategorije.map(kat => (
                        <option key={kat.id} value={kat.naziv}>{kat.naziv}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="inline-block bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-md text-[11px]">
                      {getTipologiaLabel(selectedDocForInfo.tipologia) || t('common.nd', { defaultValue: 'N/D' })}
                    </span>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('dettagli_partner.year', { defaultValue: 'Anno' })}
                  </span>
                  {isEditingDoc ? (
                    <input
                      type="number"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.anno}
                      onChange={e => setEditForm({ ...editForm, anno: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedDocForInfo.anno || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('dettagli_partner.month', { defaultValue: 'Mese' })}
                  </span>
                  {isEditingDoc ? (
                    <select
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.mese}
                      onChange={e => setEditForm({ ...editForm, mese: e.target.value })}
                    >
                      {MONTH_KEYS.map(m => (
                        <option key={m.value || 'none'} value={m.value}>
                          {m.i18nKey ? t(`months.${m.i18nKey}`, { defaultValue: m.i18nKey }) : '—'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="font-bold text-foreground">
                      {selectedDocForInfo.mese ? getMonthLabel(selectedDocForInfo.mese) : t('common.nd', { defaultValue: 'N/D' })}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('dettagli_partner.valid_from', { defaultValue: 'Valido Dal' })}
                  </span>
                  {isEditingDoc ? (
                    <input
                      type="date"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.valido_dal}
                      onChange={e => setEditForm({ ...editForm, valido_dal: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">
                      {selectedDocForInfo.valido_dal ? new Date(selectedDocForInfo.valido_dal).toLocaleDateString() : t('common.nd', { defaultValue: 'N/D' })}
                    </p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('dettagli_partner.valid_to', { defaultValue: 'Valido Al' })}
                  </span>
                  {isEditingDoc ? (
                    <input
                      type="date"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.valido_al}
                      onChange={e => setEditForm({ ...editForm, valido_al: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">
                      {selectedDocForInfo.valido_al ? new Date(selectedDocForInfo.valido_al).toLocaleDateString() : t('common.nd', { defaultValue: 'N/D' })}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-muted/30 p-3.5 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('dettagli_partner.refid', { defaultValue: 'N. Riferimento / ID' })}
                </span>
                {isEditingDoc ? (
                  <input
                    type="text"
                    className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                    placeholder={t('dettagli_partner.refid_placeholder', { defaultValue: 'Es. FATT-2026-001' })}
                    value={editForm.refid}
                    onChange={e => setEditForm({ ...editForm, refid: e.target.value })}
                  />
                ) : (
                  <p className="font-bold text-foreground font-mono">{selectedDocForInfo.refid || t('common.nd', { defaultValue: 'N/D' })}</p>
                )}
              </div>

              <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('dettagli_partner.col_date', { defaultValue: 'Data Caricamento' })}
                </span>
                <p className="font-bold text-foreground">
                  {selectedDocForInfo.created_at ? new Date(selectedDocForInfo.created_at).toLocaleString() : t('common.nd', { defaultValue: 'N/D' })}
                </p>
              </div>

              <div className="bg-muted/30 p-3.5 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('dettagli_partner.notes', { defaultValue: 'Note / Descrizione' })}
                </span>
                {isEditingDoc ? (
                  <textarea
                    className="w-full p-2.5 bg-background border border-border rounded-lg text-xs outline-none min-h-[70px]"
                    placeholder={t('dettagli_partner.notes_placeholder', { defaultValue: 'Specifica di cosa si tratta...' })}
                    value={editForm.note}
                    onChange={e => setEditForm({ ...editForm, note: e.target.value })}
                  />
                ) : (
                  <p className="font-medium text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {selectedDocForInfo.note || t('dettagli_partner.no_notes', { defaultValue: 'Nessuna nota aggiunta.' })}
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              {isEditingDoc ? (
                <>
                  <button
                    onClick={() => setIsEditingDoc(false)}
                    disabled={savingDoc}
                    className="px-5 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-all disabled:opacity-50"
                  >
                    {t('common.cancel', { defaultValue: 'Annulla' })}
                  </button>
                  <button
                    onClick={handleSaveDocEdits}
                    disabled={savingDoc}
                    className="bg-primary text-primary-foreground px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-all shadow-md shadow-primary/20 flex items-center gap-2 disabled:opacity-50"
                  >
                    {savingDoc
                      ? <><Icon name="Loader2" size={16} className="animate-spin" />{t('common.saving', { defaultValue: 'Salvataggio...' })}</>
                      : <><Icon name="Check" size={16} />{t('common.save', { defaultValue: 'Salva' })}</>
                    }
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setSelectedDocForInfo(null); setIsEditingDoc(false); }}
                    className="px-5 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                  >
                    {t('common.close', { defaultValue: 'Chiudi' })}
                  </button>
                  <div className="flex gap-2">
                    {/* ANTEPRIMA dugme */}
                    <button
                      onClick={() => handleAnteprima(selectedDocForInfo)}
                      disabled={anteorimaLoading}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-muted border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted/80 transition-all disabled:opacity-50"
                    >
                      {anteorimaLoading ? (
                        <>
                          <Icon name="Loader2" size={16} className="animate-spin" /> {t('common.loading', { defaultValue: 'Caricamento...' })}
                        </>
                      ) : (
                        <>
                          <Icon name="Eye" size={16} /> {t('common.preview', { defaultValue: 'Anteprima' })}
                        </>
                      )}
                    </button>
                    {/* DOWNLOAD dugme */}
                    <button
                      onClick={() => handleDownload(selectedDocForInfo.id, selectedDocForInfo.file_path, selectedDocForInfo.nome_file)}
                      disabled={downloadingId === selectedDocForInfo.id}
                      className="bg-primary text-primary-foreground px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-all shadow-md shadow-primary/20 flex items-center gap-2 disabled:opacity-50"
                    >
                      {downloadingId === selectedDocForInfo.id
                        ? <><Icon name="Loader2" size={16} className="animate-spin" />{t('common.downloading', { defaultValue: 'Scaricamento...' })}</>
                        : <><Icon name="Download" size={16} />{t('common.download', { defaultValue: 'Scarica' })}</>
                      }
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DettagliPartner;